import type {MouseEvent} from 'react';
import type {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {DesignSelection, RefreshAccountResponse, SocialAccountConnectionResponse} from '@Components/social-media/social-media.types';
import {SocialAccountConnectionResult, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {MAX_POSTERS_PER_PLATFORM} from '@Components/social-media/account.vo';
import {getUserHashedId, getUserId, getUserPremiumLevel, isUserLoggedIn} from '@Libraries/user.library';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {getAjaxErrorCode} from '@Utils/ajax.util';
import {ErrorUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';
import {isIOSApp, isMobileOrIOSApp} from '@Utils/browser.util';
import {openConfirmationModal} from '@Modals/confirmation-modal';
import {openErrorModal} from '@Modals/error-modal';
import type {VoidFunction} from '@/utils/general.util';
import {asyncnoop, noop} from '@/utils/general.util';
import {closeSocialAccountConnectionResponseModal, openSocialAccountConnectionResponseModal} from '@Modals/social-account-connection-response-modal';
import {dismissIOSSafariView, openLoginWindow} from '@Libraries/login-signup-library';

export const MAX_PAYG_PARENT_ACCOUNTS = 1;
export const MAX_PREMIUM_LEVEL_1_PARENT_ACCOUNTS = 3;
const OAUTH_POPUP_CLOSE_POLL_MS = 700;
const META_PLATFORM_FAMILY: SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.FACEBOOK_PROFILE,
  SocialPlatform.FACEBOOK_PAGE,
  SocialPlatform.FACEBOOK_GROUP,
  SocialPlatform.INSTAGRAM,
];
const LINKEDIN_PLATFORM_FAMILY: SocialPlatform[] = [SocialPlatform.LINKEDIN, SocialPlatform.LINKEDIN_PROFILE, SocialPlatform.LINKEDIN_PAGE];

export enum SocialMediaUpsellEvent {
  ACCOUNTS_PREMIUM_PLUS = 'SocialWizard_Upselling_Accounts_Premium',
  ACCOUNTS_PREMIUM = 'SocialWizard_Upselling_Accounts_Premium_Plus',
  SCHEDULE = 'SocialWizard_Upselling_Schedule',
}

export const getSocialAccountsUpsellByPremiumLevel = (): SocialMediaUpsellEvent => {
  if (window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PREMIUM) {
    return SocialMediaUpsellEvent.ACCOUNTS_PREMIUM_PLUS;
  }

  return SocialMediaUpsellEvent.ACCOUNTS_PREMIUM;
};

export const doesAccountHaveCarouselPosts = (accountType: SocialPlatform | undefined): boolean => {
  switch (accountType) {
    case SocialPlatform.INSTAGRAM:
    case SocialPlatform.PINTEREST:
    case SocialPlatform.THREADS:
      return true;
    default:
      return false;
  }
};

export const onConnectAccount = (accountType: SocialPlatform): void => {
  switch (accountType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      openOAuthPopup('socialmedia/facebookOAuth');
      break;
    case SocialPlatform.INSTAGRAM:
      openOAuthPopup('socialmedia/instagramBusinessOAuth');
      break;
    case SocialPlatform.YOUTUBE:
      onConnectYouTube();
      break;
    case SocialPlatform.TWITTER:
      openOAuthPopup('socialmedia/twitterOAuth');
      break;
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      openOAuthPopup('socialmedia/linkedInOAuth');
      break;
    case SocialPlatform.TIKTOK:
      openOAuthPopup('socialmedia/tiktokOAuth');
      break;
    case SocialPlatform.PINTEREST:
      openOAuthPopup('socialmedia/pinterestOAuth');
      break;
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      openOAuthPopup('socialmedia/googleBusinessProfileOAuth');
      break;
    case SocialPlatform.THREADS:
      openOAuthPopup('socialmedia/threadsOAuth');
      break;
    default:
      console.error('Illegal account type');
  }
};

export const getAuthUriForAccountType = (accountType: SocialPlatform): string => {
  switch (accountType) {
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.FACEBOOK_GROUP:
      return 'socialmedia/getFacebookOAuthUrl';
    case SocialPlatform.INSTAGRAM:
      return 'socialmedia/getInstagramBusinessOauthUrl';
    case SocialPlatform.YOUTUBE:
      return 'socialmedia/getYoutubeOAuthUrl';
    case SocialPlatform.TWITTER:
      return 'socialmedia/getTwitterOAuthUrl';
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
      return 'socialmedia/getLinkedInOAuthUrl';
    case SocialPlatform.TIKTOK:
      return 'socialmedia/getTiktokOAuthUrl';
    case SocialPlatform.PINTEREST:
      return 'socialmedia/getPinterestOAuthUrl';
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
      return 'socialmedia/getGoogleBusinessProfileOAuthUrl';
    case SocialPlatform.THREADS:
      return 'socialmedia/getThreadsOAuthUrl';
    default:
      throw new Error(`Illegal account type${accountType}`);
  }
};

const getAuthUrl = async (accountType: SocialPlatform): Promise<string> => {
  return (await window.PMW.readLocal(getAuthUriForAccountType(accountType), {
    isIOSApp: true,
  })) as string;
};

const initAccountConnection = async (accountType: SocialPlatform, onAccountCreated: () => void): Promise<void> => {
  await openLoginWindow(getAuthUrl.bind(null, accountType), noop);
  const ACCOUNT_CONNECTION_TIMEOUT_MS = 300000;
  const connectionData = (await window.PMW.pollingAjaxCallAsync(
    asyncnoop,
    () => {
      return window.PMW.readLocal('socialmedia/hasAccountBeenAdded', {
        platform: accountType,
      });
    },
    ACCOUNT_CONNECTION_TIMEOUT_MS
  )) as SocialAccountConnectionResponse | null;

  const validatedConnectionData = validateAccountAddedResponse(connectionData);
  broadcastAccountConnectionResponse(validatedConnectionData);
  handleAccountConnectionResponse(validatedConnectionData, onAccountCreated);
};

/**
 * The mobile/iOS polling flow bypasses the OAuth popup that normally posts to the connect BroadcastChannel,
 * so it re-broadcasts here to keep every listening surface reacting to both flows identically.
 */
const broadcastAccountConnectionResponse = (connectionResponse: SocialAccountConnectionResponse | null): void => {
  const broadcastName = getConnectAccountBroadcastName();

  if (!connectionResponse || !broadcastName) {
    return;
  }

  const broadcastChannel = new BroadcastChannel(broadcastName);
  broadcastChannel.postMessage(connectionResponse);
  broadcastChannel.close();
};

const validateAccountAddedResponse = (response: SocialAccountConnectionResponse | null | string): SocialAccountConnectionResponse | null => {
  if (typeof response === 'string') {
    return JSON.parse(response) as SocialAccountConnectionResponse;
  }

  return response;
};

export const startAccountConnection = (accountType: SocialPlatform, onAccountCreated: () => void): void => {
  if (isMobileOrIOSApp()) {
    void onConnectAccountForIosApp(accountType, onAccountCreated);
    return;
  }

  onConnectAccount(accountType);
};

export const onConnectAccountForIosApp = async (accountType: SocialPlatform, onAccountCreated: () => void): Promise<void> => {
  if (accountType === SocialPlatform.YOUTUBE) {
    onConnectYouTube(initAccountConnection.bind(null, accountType, onAccountCreated));
  } else {
    await initAccountConnection(accountType, onAccountCreated);
  }
};

const onConnectYouTube = (initiateConnection?: () => Promise<void>): void => {
  openConfirmationModal({
    onConfirm: (): void => {
      if (initiateConnection) {
        void initiateConnection();
      } else {
        openOAuthPopup('socialmedia/youtubeOAuth');
      }
    },
    icon: 'icon-check',
    infoText: window.i18next.t('pmwjs_youtube_agreement_point', {
      ytCommunityGuidelines: 'https://www.youtube.com/howyoutubeworks/policies/community-guidelines/',
      googlePrivacyPolicy: 'http://www.google.com/policies/privacy',
      ytTermsOfService: 'https://www.youtube.com/t/terms',
      privacyPolicy: 'https://www.postermywall.com/index.php/info/termsofuse',
      googleSecuritySettings: 'https://security.google.com/settings/security/permissions',
    }),
    doShowContentIcon: false,
    confirmButtonText: window.i18next.t('pmwjs_agree_to_all'),
    panelTitle: window.i18next.t('pmwjs_add_youtube_channel'),
  });
};

export const isTwitterCurrentlyUnusable = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.TWITTER) {
    // TODO: switch to false when we're back up
    return false;
  }

  return false;
};

export const isPostersCountValidForMultiPosting = (posters: DesignSelection[], account: SocialAccount): boolean => {
  const accountPlatform = account.type;
  const maxNumberOfPosts = MAX_POSTERS_PER_PLATFORM[accountPlatform];

  if (maxNumberOfPosts === null) {
    return true;
  }

  return posters.length <= maxNumberOfPosts;
};

export const isYouTubeCurrentlyUnusable = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.YOUTUBE) {
    // TODO: switch to false when we're back up
    return false;
  }

  return false;
};

export const isGoogleBusinessProfileAccountUnverified = (account: SocialAccount): boolean => {
  if (account.type === SocialPlatform.GOOGLE_BUSINESS_PROFILE) {
    return !account.isVerifiedGoogleBusinessProfileAccount;
  }

  return false;
};

const openOAuthPopup = (oAuthURI: string): void => {
  window.open(window.PMW.util.site_url(oAuthURI), '_blank');
};

export const sortPlanLockedAccountsLast = (accounts: SocialAccount[]): SocialAccount[] => {
  if (accounts.length <= 1) {
    return accounts;
  }

  return [...accounts].sort(comparePlanLockedLast);
};

export const compareByPlanKeepPreference = (first: SocialAccount, second: SocialAccount): number => {
  return comparePlanLockedLast(first, second) || second.id - first.id;
};

const comparePlanLockedLast = (first: SocialAccount, second: SocialAccount): number => {
  return Number(first.isLockedByPlanLimit) - Number(second.isLockedByPlanLimit);
};

export const isParentSocialAccount = (account: SocialAccount): boolean => {
  return account.type !== SocialPlatform.FACEBOOK_PAGE && account.type !== SocialPlatform.FACEBOOK_GROUP;
};

export const getNumParentAccounts = (accounts: SocialAccount[]): number => {
  return accounts.filter(isParentSocialAccount).length;
};

/** Backend count wins when the loaded list omits expired and profile accounts. */
export const getNumConnectedParentAccounts = (backendParentAccountCount: number, loadedAccounts: SocialAccount[]): number => {
  return Math.max(backendParentAccountCount, getNumParentAccounts(loadedAccounts));
};

export const getMaxParentAccountsForPlan = (): number | null => {
  const premiumLevel = getUserPremiumLevel();

  if (premiumLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    return null;
  }

  return premiumLevel === USER_PREMIUM_LEVELS.PREMIUM ? MAX_PREMIUM_LEVEL_1_PARENT_ACCOUNTS : MAX_PAYG_PARENT_ACCOUNTS;
};

export const getPlanLockedAccountMessage = (): string => {
  return window.i18next.t('pmwjs_plan_limit_active_accounts_message', {count: getMaxParentAccountsForPlan() ?? MAX_PAYG_PARENT_ACCOUNTS});
};

export const isEligibleForSocialAccountsUpsellDialog = (numAccounts: number): boolean => {
  const maxParentAccounts = getMaxParentAccountsForPlan();

  if (maxParentAccounts === null) {
    return false;
  }

  return numAccounts >= maxParentAccounts;
};

const onPremiumUpsellClicked = (eventToTrack = ''): void => {
  if (eventToTrack) {
    window.PMW.gtm.trackGA4CustomEvent(eventToTrack);
  }
};

const getUpsellDialogNameByPremiumLevel = (userPremiumLevel: USER_PREMIUM_LEVELS): string => {
  if (userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
    return window.PMW.PREMIUM_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS as string;
  }

  return window.PMW.PREMIUM_PLUS_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS as string;
};

interface SocialMediaPublishingUpsellOptions {
  userPremiumLevel?: number;
  entryPoint?: UpsellDialogEntryPoint;
}

const showPremiumUpsellDialogForFeature = (featureName: string, eventToTrack: string, entryPoint?: UpsellDialogEntryPoint): void => {
  const eventName = window.PMW.getPremiumUpsellClickEvent(featureName);
  const eventHandler = (): void => {
    onPremiumUpsellClicked(eventToTrack);
  };

  void window.PMW.showPremiumOnlyFeatureDialog(featureName, {entryPoint});
  window.addEventListener(eventName, eventHandler, {
    once: true,
  });
};

export const showSocialMediaPublishingUpsellDialog = (eventToTrack = '', {userPremiumLevel = USER_PREMIUM_LEVELS.PAYG, entryPoint}: SocialMediaPublishingUpsellOptions = {}): void => {
  showPremiumUpsellDialogForFeature(getUpsellDialogNameByPremiumLevel(userPremiumLevel), eventToTrack, entryPoint);
};

export const showSocialMediaSchedulingUpsellDialog = (entryPoint: UpsellDialogEntryPoint): void => {
  showPremiumUpsellDialogForFeature(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SCHEDULE_POST, SocialMediaUpsellEvent.SCHEDULE, entryPoint);
};

export const openSocialAccountsUpsellDialog = (entryPoint: UpsellDialogEntryPoint): void => {
  showSocialMediaPublishingUpsellDialog(getSocialAccountsUpsellByPremiumLevel(), {userPremiumLevel: getUserPremiumLevel(), entryPoint});
};

export const isPlanLockedRefusal = (error: unknown): boolean => {
  return getAjaxErrorCode(error) === ErrorUpdatePublishingParams.SOCIAL_ACCOUNT_PLAN_LOCKED;
};

/** For an upgrade control nested in a clickable card: the click opens the upsell without also opening the card. */
export const openSocialAccountsUpsellDialogFromNestedControl = (event: MouseEvent, entryPoint: UpsellDialogEntryPoint): void => {
  event.stopPropagation();
  openSocialAccountsUpsellDialog(entryPoint);
};

export const shareSocialMediaAccountInTeam = async (isSharedInTeam: boolean, idAccount: number): Promise<void> => {
  if (isUserLoggedIn()) {
    await window.PMW.writeLocal('socialmedia/updateSocialMediaAccountSharingStatus', {
      shareInTeam: isSharedInTeam,
      idAccount: idAccount,
    });
  }
};

const reAuthenticateForIOS = async (idAccount: number, accountType: SocialPlatform): Promise<void> => {
  try {
    const response = (await window.PMW.readLocal('socialmedia/refreshAccountForIOSApp', {
      idAccount,
      platform: accountType,
    })) as RefreshAccountResponse;
    if (response.url) {
      window.open(response.url, '_blank');
    }
  } catch {
    openErrorModal();
  }
};

/** A blocked popup never broadcasts nor reports closed; iOS legitimately hands back null and still reports. */
const isOAuthPopupBlocked = (): boolean => {
  return !isIOSApp();
};

export const reAuthenticateInModal = (idAccount: number, accountType: SocialPlatform): Window | null => {
  if (isIOSApp()) {
    void reAuthenticateForIOS(idAccount, accountType);
    return null;
  }

  return window.open(
    window.PMW.util.site_url(`socialmedia/refreshAccount?idAccount=${idAccount}&platform=${accountType}`),
    '',
    'width=600,height=600,top=0,=right=50%,=scrollbars=yes'
  );
};

export const isAccountInSearch = (searchTerm: string | undefined, accountName: string): boolean => {
  if (!searchTerm || searchTerm === '') {
    return true;
  }

  // Convert both the account name and searchTerm to lowercase for case-insensitive matching
  const lowerCaseAccountName = accountName.toLowerCase();
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  return lowerCaseAccountName.includes(lowerCaseSearchTerm);
};

/**
 * The stored account types a single OAuth grant can create together, which is wider than
 * `getComparableSocialPlatform`: a Meta grant — the Facebook connect and the legacy Instagram connect alike —
 * saves the Facebook profile, its pages and groups AND its Instagram business accounts (Socialmedia's
 * revalidateFacebookPages / revalidateIgBusinesses), so the post-connect selection has to offer all of them.
 */
const getAccountTypesInPlatformFamily = (platform: SocialPlatform): SocialPlatform[] => {
  if (META_PLATFORM_FAMILY.includes(platform)) {
    return META_PLATFORM_FAMILY;
  }

  if (LINKEDIN_PLATFORM_FAMILY.includes(platform)) {
    return LINKEDIN_PLATFORM_FAMILY;
  }

  return [platform];
};

export const getAccountIdsForPlatformFamily = (accounts: SocialAccount[], platform: SocialPlatform): number[] => {
  const accountTypesInFamily = getAccountTypesInPlatformFamily(platform);

  return accounts
    .filter((account) => {
      return accountTypesInFamily.includes(account.type);
    })
    .map((account) => {
      return account.id;
    });
};

export const getNewlyConnectedAccountIds = (accountsBeforeConnection: SocialAccount[], accountsAfterConnection: SocialAccount[]): number[] => {
  const accountIdsBeforeConnection = new Set(
    accountsBeforeConnection.map((account) => {
      return account.id;
    })
  );

  return accountsAfterConnection
    .filter((account) => {
      return !accountIdsBeforeConnection.has(account.id);
    })
    .map((account) => {
      return account.id;
    });
};

export const isSocialMediaAccountSharedByTeamMember = (account: SocialAccount): boolean => {
  return account.isSharedInTeam && account.idUser !== getUserId();
};

export const addConnectAccountBroadcastChannel = (onSuccessMessageReceived: (eventData: MessageEvent) => void): BroadcastChannel | undefined => {
  const accountCreatedBroadcastName = getConnectAccountBroadcastName();

  if (!accountCreatedBroadcastName) {
    return;
  }

  const accountCreatedBroadcastChannel = new BroadcastChannel(accountCreatedBroadcastName);
  accountCreatedBroadcastChannel.addEventListener('message', onSuccessMessageReceived);

  return accountCreatedBroadcastChannel;
};

export const removeConnectAccountBroadcastChannel = (accountCreatedBroadcastChannel?: BroadcastChannel): void => {
  if (!accountCreatedBroadcastChannel) {
    return;
  }

  accountCreatedBroadcastChannel.close();
};

export const getConnectAccountBroadcastName = (): string => {
  const hashedUserId = getUserHashedId();
  if (hashedUserId) {
    return hashedUserId;
  }

  return '';
};

export const getAccountConnectionResponseFromBroadcast = (broadcastEvent: MessageEvent): SocialAccountConnectionResponse | null => {
  const connectionResponse: SocialAccountConnectionResponse = broadcastEvent.data as SocialAccountConnectionResponse;

  if (!connectionResponse) {
    return null;
  }

  return connectionResponse;
};

export const handleAccountConnectionResponse = (
  connectionResponse: SocialAccountConnectionResponse | null,
  onSuccess: VoidFunction = noop,
  onNothingHappened: VoidFunction = noop,
  onFailure: VoidFunction = noop
): void => {
  dismissIOSSafariView();
  if (!connectionResponse) {
    return;
  }

  if (connectionResponse.hideExistingModal) {
    closeSocialAccountConnectionResponseModal();
  }

  if (connectionResponse.socialAccountConnectionResult === SocialAccountConnectionResult.NOTHING_HAPPENED) {
    onNothingHappened();
    return;
  }

  if (connectionResponse.socialAccountConnectionResult === SocialAccountConnectionResult.ACCOUNT_ADDED_SUCCESS) {
    onSuccess();
    return;
  }

  onFailure();
  openSocialAccountConnectionResponseModal({connectionResponse: connectionResponse});
};

export interface ReconnectWithBroadcastOpts {
  idAccount: number;
  accountType: SocialPlatform;
  previousChannel: BroadcastChannel | undefined;
  onSuccess: VoidFunction;
  onNothingHappened: VoidFunction;
  onFailure: VoidFunction;
}

/**
 * Single reconnect handshake: closes any previously-opened broadcast channel (so repeated
 * reconnects on the same account never leak a listener), opens a fresh one wired to the
 * connection-response callbacks, then triggers the reAuthenticateInModal OAuth popup.
 * Returns the new channel so the caller can hold it for the next call and for unmount cleanup.
 */
export const runReconnectWithBroadcast = (opts: ReconnectWithBroadcastOpts): BroadcastChannel | undefined => {
  const {idAccount, accountType, previousChannel, onSuccess, onNothingHappened, onFailure} = opts;

  removeConnectAccountBroadcastChannel(previousChannel);

  let isResolved = false;
  let popupClosePollId: ReturnType<typeof setInterval> | undefined;

  // The first of {broadcast result, popup closed} wins; a genuine success/failure broadcast always fires before the popup-closed poll ticks, so closing the window is only treated as abandonment when no result arrived.
  const resolveOnce = (handler: VoidFunction): void => {
    if (isResolved) {
      return;
    }
    isResolved = true;
    if (popupClosePollId !== undefined) {
      clearInterval(popupClosePollId);
    }
    handler();
  };

  const onMessage = (event: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(event);
    handleAccountConnectionResponse(
      connectionResponse,
      () => {
        resolveOnce(onSuccess);
      },
      () => {
        resolveOnce(onNothingHappened);
      },
      () => {
        resolveOnce(onFailure);
      }
    );
  };

  const channel = addConnectAccountBroadcastChannel(onMessage);
  const oAuthPopup = reAuthenticateInModal(idAccount, accountType);

  if (oAuthPopup) {
    popupClosePollId = setInterval((): void => {
      if (oAuthPopup.closed) {
        resolveOnce(onNothingHappened);
      }
    }, OAUTH_POPUP_CLOSE_POLL_MS);
  } else if (isOAuthPopupBlocked()) {
    resolveOnce(onNothingHappened);
  }

  return channel;
};
